import dayjs, { Dayjs } from 'dayjs';

export const MAX_DATE_RANGE_DAYS = 100;
export const MAX_HISTORY_YEARS = 3;

const API_DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';

export interface SpecificFilters {
  nameOrDni?: string | null;
  cartId?: string | null;
  proposalReference?: string | null;
}

export interface DateRange {
  fromDate: string;
  toDate: string;
}

// Buscadores específicos visibles en el dashboard (nombre/DNI siempre lo está)
export interface AvailableSpecificFilters {
  cartId: boolean;
  proposalReference: boolean;
}

const ALL_SPECIFIC_FILTERS: AvailableSpecificFilters = {
  cartId: true,
  proposalReference: true,
};

export const getRangeTooWideTitle = (): string => `El intervalo no puede superar ${MAX_DATE_RANGE_DAYS} días`;

// Buscadores específicos visibles, p. ej. "nombre, DNI/NIE, ID de carrito o código AG"
export const getSpecificFiltersLabel = (
  { cartId, proposalReference }: AvailableSpecificFilters = ALL_SPECIFIC_FILTERS,
): string => {
  const fields = [
    'nombre',
    'DNI/NIE',
    ...(cartId ? ['ID de carrito'] : []),
    ...(proposalReference ? ['código AG'] : []),
  ];
  const lastField = fields.pop();
  return `${fields.join(', ')} o ${lastField}`;
};

export const getRangeTooWideHint = (
  availableFilters: AvailableSpecificFilters = ALL_SPECIFIC_FILTERS,
): string => `Para consultar un periodo más amplio, busca por ${getSpecificFiltersLabel(availableFilters)}.`;

export enum DateRangeErrorType {
  Invalid = 'INVALID',
  Inverted = 'INVERTED',
  OutOfHistory = 'OUT_OF_HISTORY',
  RangeTooWide = 'RANGE_TOO_WIDE',
  Future = 'FUTURE',
}

export interface DateRangeError {
  type: DateRangeErrorType;
  message: string;
}

// DNI/NIE, nombre, ID de carrito o código AG: si alguno tiene valor, se permite
// buscar en todo el histórico sin el límite de 100 días.
export const hasSpecificFilter = (filters: SpecificFilters): boolean => Object.values(filters)
  .some((value) => !!value?.trim());

// Primer día consultable: con hoy 23/10/2026 es el 24/10/2023 (el mismo día de hace
// 3 años ya queda fuera)
export const getMinAllowedDate = (today: Dayjs = dayjs()): Dayjs => today
  .startOf('day')
  .subtract(MAX_HISTORY_YEARS, 'year')
  .add(1, 'day');

export const getDateRangeError = (
  { fromDate, toDate }: DateRange,
  specificFilter: boolean,
  today: Dayjs = dayjs(),
): DateRangeError | null => {
  // Un rango incompleto todavía se está seleccionando: no se valida ni se envía
  if (!fromDate || !toDate) {
    return null;
  }
  const from = dayjs(fromDate);
  const to = dayjs(toDate);
  const minDate = getMinAllowedDate(today);

  if (!from.isValid() || !to.isValid()) {
    return {
      type: DateRangeErrorType.Invalid,
      message: 'Las fechas seleccionadas no son válidas.',
    };
  }
  if (from.isAfter(to, 'day')) {
    return {
      type: DateRangeErrorType.Inverted,
      message: 'La fecha de inicio no puede ser posterior a la fecha de fin.',
    };
  }
  if (from.isBefore(minDate, 'day')) {
    return {
      type: DateRangeErrorType.OutOfHistory,
      message: `Solo puedes consultar operaciones de los últimos ${MAX_HISTORY_YEARS} años (desde el ${minDate.format(DISPLAY_DATE_FORMAT)}).`,
    };
  }
  if (!specificFilter && to.diff(from, 'day') > MAX_DATE_RANGE_DAYS) {
    return {
      type: DateRangeErrorType.RangeTooWide,
      message: `El intervalo no puede superar ${MAX_DATE_RANGE_DAYS} días. ${getRangeTooWideHint()}`,
    };
  }
  if (to.isAfter(today, 'day')) {
    return {
      type: DateRangeErrorType.Future,
      message: 'La fecha de fin no puede ser posterior a hoy.',
    };
  }
  return null;
};

// Rango que se envía a la API: el seleccionado o, sin fechas, el máximo permitido
export const getEffectiveDateRange = (
  { fromDate, toDate }: DateRange,
  specificFilter: boolean,
  today: Dayjs = dayjs(),
): DateRange => {
  if (fromDate && toDate) {
    return { fromDate, toDate };
  }
  const from = specificFilter
    ? getMinAllowedDate(today)
    : today.startOf('day').subtract(MAX_DATE_RANGE_DAYS, 'day');
  return {
    fromDate: from.format(API_DATE_FORMAT),
    toDate: today.format(API_DATE_FORMAT),
  };
};

// Alcance aplicado cuando no hay fechas seleccionadas
export const getSearchScopePeriod = (specificFilter: boolean): string => (specificFilter
  ? `${MAX_HISTORY_YEARS} años`
  : `${MAX_DATE_RANGE_DAYS} días`);
